/* pulse-core.js — обработка сигнала пульса. Отдельно от UI, чтобы доказуемо.
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ
 * --------------------
 * Тул выдаёт число, которому человек поверит про своё тело. Значит алгоритм
 * обязан быть проверяемым на сигнале с ИЗВЕСТНОЙ частотой, а не «ну вроде
 * похоже на мой пульс». Здесь только математика: на вход — ряд яркостей
 * красного канала, на выход — частота и ЧЕСТНАЯ оценка качества.
 *
 * ГЛАВНОЕ РЕШЕНИЕ: МЫ ИМЕЕМ ПРАВО НЕ ОТВЕТИТЬ.
 * Приложения показывают число всегда — при любом сигнале, даже когда палец
 * съехал. Это хуже отсутствия ответа: человек считает мусор своим пульсом.
 * Поэтому здесь есть порог качества, и ниже него функция возвращает
 * { ok:false, reason }, а не «ближайший пик шума».
 *
 * МЕТОД (переделка 2026-10-28, отзыв #2034 «Не показывает число»):
 * полосовой Баттерворт 0.7–3.5 Гц вперёд-назад → спектр по сетке частот →
 * основная частота по ГАРМОНИЧЕСКОЙ сумме → качество = доля мощности полосы,
 * собранная в основной и второй гармониках (SNR). Молчим, если SNR мал.
 *
 * ⚠️ Почему не автокорреляция, как было. Прежний «полосовой» фильтр был
 * разностью скользящих средних — то есть ФИЛЬТРОМ ВЕРХНИХ ЧАСТОТ на 3.5 Гц:
 * пульс 1–2 Гц он ослаблял до 15–25 %, а шум камеры пропускал целиком. На
 * чистой синтетике это не видно, на живом пальце — видно сразу. Замер на
 * BUT PPG (PhysioNet, 3 888 настоящих записей со смартфонов, эталон — ЭКГ,
 * эксперты пометили годность), путь «как страница» (rAF с дублями кадров),
 * окна пальца 20 / 30 с, годные записи:
 *                        верно ±5      ложь
 *   прежний              37 / 50 %   1.9 / 1.1 %
 *   этот                 см. docs/why/pulse-meter.md — вдвое выше, ложь ≈ 0
 * На проде прежний отказывал «шумно» в 4 случаях из 5 (566 против 133 чисел
 * за 2.5 месяца на RU). Голая автокорреляция с исправленным фильтром тоже
 * хуже спектра: её «уверенность» (corr) хуже отделяет годное от негодного.
 * Стенд замера — tests/js/pulse-core.test.mjs держит выводы на синтетике,
 * сам датасет в репозиторий не кладём (разбор — docs/why/pulse-meter.md).
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof root !== 'undefined') root.PulseCore = api;
}(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var MIN_BPM = 42, MAX_BPM = 210;
  var BAND_LO = 0.7, BAND_HI = 3.5;          // Гц: 42–210 уд/мин
  var SPEC_LO = 0.5, SPEC_HI = 4.2, SPEC_DF = 0.01;
  // Пороги SNR — замеренные, не на глаз (см. шапку). Итог 25 с — 0.55: по
  // ВСЕМ окнам 20–30 с (годным и негодным) точность ±5 — 83–90 % против
  // 73–82 % у прежнего, а отвечает он в 1.5–2 раза чаще. Предварительное
  // число считается по 8–25 с, а на 10 с при 0.5 лгали 12 % годных записей,
  // при 0.65 — 4 %.
  var FINAL_SNR = 0.55, PRELIM_SNR = 0.65;

  /** Биквад по RBJ cookbook: 'lp' или 'hp', добротность Баттерворта. */
  function biquad(type, f0, fs) {
    var w = 2 * Math.PI * f0 / fs, c = Math.cos(w), s = Math.sin(w), a = s / (2 * Math.SQRT1_2);
    var b0, b1, b2;
    if (type === 'lp') { b0 = (1 - c) / 2; b1 = 1 - c; b2 = (1 - c) / 2; }
    else { b0 = (1 + c) / 2; b1 = -(1 + c); b2 = (1 + c) / 2; }
    var a0 = 1 + a;
    return [b0 / a0, b1 / a0, b2 / a0, (-2 * c) / a0, (1 - a) / a0];
  }

  function runBiquad(x, k) {
    var b0 = k[0], b1 = k[1], b2 = k[2], a1 = k[3], a2 = k[4];
    var y = new Float64Array(x.length);
    // Старт из установившегося состояния: иначе первая секунда — переходный
    // процесс от нуля до уровня красного (~200), и он забивает спектр.
    var x1 = x[0], x2 = x[0], y1 = 0, y2 = 0;
    var dc = (b0 + b1 + b2) / (1 + a1 + a2);
    if (Math.abs(b0 + b1 + b2) > 1e-9) { y1 = y2 = x[0] * dc; }
    for (var i = 0; i < x.length; i++) {
      var v = b0 * x[i] + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2;
      x2 = x1; x1 = x[i]; y2 = y1; y1 = v; y[i] = v;
    }
    return y;
  }

  /**
   * Полосовой 0.7–3.5 Гц без сдвига фазы (вперёд, потом назад). Края
   * отражаются на 2 с: запись короткая, и переходный процесс фильтра иначе
   * съел бы заметную её часть.
   */
  function bandpass(values, fps) {
    var n = values.length;
    var pad = Math.min(n - 1, Math.round(fps * 2));
    var ext = new Float64Array(n + 2 * pad), i;
    for (i = 0; i < pad; i++) {
      ext[i] = 2 * values[0] - values[pad - i];
      ext[ext.length - 1 - i] = 2 * values[n - 1] - values[n - 1 - pad + i];
    }
    for (i = 0; i < n; i++) ext[pad + i] = values[i];
    var hp = biquad('hp', BAND_LO, fps), lp = biquad('lp', BAND_HI, fps);
    var y = runBiquad(runBiquad(ext, hp), lp);
    Array.prototype.reverse.call(y);
    y = runBiquad(runBiquad(y, hp), lp);
    Array.prototype.reverse.call(y);
    return y.subarray(pad, pad + n);
  }

  /** Мощность по сетке частот (окно Ханна). Прямой ДПФ: длина любая. */
  function spectrum(sig, fs) {
    var n = sig.length, w = new Float64Array(n), mean = 0, i;
    for (i = 0; i < n; i++) mean += sig[i];
    mean /= n;
    for (i = 0; i < n; i++) w[i] = (sig[i] - mean) * (0.5 - 0.5 * Math.cos(2 * Math.PI * i / (n - 1)));
    var bins = Math.floor((SPEC_HI - SPEC_LO) / SPEC_DF) + 1;
    var pow = new Float64Array(bins);
    for (var b = 0; b < bins; b++) {
      var om = 2 * Math.PI * (SPEC_LO + b * SPEC_DF) / fs;
      var cr = Math.cos(om), ci = Math.sin(om), pr = 1, pi = 0, re = 0, im = 0;
      for (i = 0; i < n; i++) {
        re += w[i] * pr; im -= w[i] * pi;
        var t = pr * cr - pi * ci; pi = pr * ci + pi * cr; pr = t;
      }
      pow[b] = re * re + im * im;
    }
    return pow;
  }

  function binOf(f) { return Math.round((f - SPEC_LO) / SPEC_DF); }
  function powAt(pow, f) { var i = binOf(f); return (i >= 0 && i < pow.length) ? pow[i] : 0; }
  function bandPow(pow, f, hw) {
    var a = Math.max(0, binOf(f - hw)), b = Math.min(pow.length - 1, binOf(f + hw)), s = 0;
    for (var i = a; i <= b; i++) s += pow[i];
    return s;
  }

  /**
   * Главная функция: ряд яркостей → пульс или честный отказ.
   *
   * @param {Array<number>} samples яркость красного канала по кадрам
   * @param {number} fps            частота РАВНОМЕРНОЙ сетки отсчётов
   * @param {{prelim?:boolean}} [opts] prelim — промежуточное число по
   *        короткой записи: порог строже (см. PRELIM_SNR)
   * @returns {{ok:boolean, bpm?:number, quality?:number, snr?:number, reason?:string}}
   */
  function estimate(samples, fps, opts) {
    if (!samples || samples.length < fps * 6) return { ok: false, reason: 'too_short' };
    var n = samples.length, i;
    var sig = bandpass(Array.prototype.slice.call(samples), fps);

    // Застывший ряд: камера не отдаёт новых кадров (свернули, перехватил
    // звонок). Это единственный случай, когда судит АМПЛИТУДА: прежний порог
    // rms < 0.05 отвергал 4 % годных записей пальца — громкость сигнала про
    // его годность ничего не говорит, говорит SNR ниже.
    var e = 0;
    for (i = 0; i < n; i++) e += sig[i] * sig[i];
    var rms = Math.sqrt(e / n);
    if (!(rms > 1e-4)) return { ok: false, reason: 'weak_signal', rms: rms };

    var pow = spectrum(sig, fps);
    var lo = binOf(MIN_BPM / 60), hi = binOf(MAX_BPM / 60);
    var best = lo, bestScore = -1;
    for (i = lo; i <= hi; i++) {
      var f = SPEC_LO + i * SPEC_DF;
      // Гармоническая сумма: настоящая основная собирает и свою вторую
      // гармонику (дикротический зубец), а пик на 2f — нет. Иначе сильная
      // вторая гармоника давала бы удвоенный пульс.
      var sc = pow[i] + 0.5 * powAt(pow, 2 * f);
      if (sc > bestScore) { bestScore = sc; best = i; }
    }
    // Параболическое уточнение вершины: шаг сетки — 0.6 уд/мин.
    var f0 = SPEC_LO + best * SPEC_DF;
    var y0 = pow[best - 1] || 0, y1 = pow[best], y2 = pow[best + 1] || 0, den = y0 - 2 * y1 + y2;
    if (den < 0) {
      var sh = 0.5 * (y0 - y2) / den;
      if (Math.abs(sh) <= 1) f0 += sh * SPEC_DF;
    }

    var all = 0;
    for (i = 0; i < pow.length; i++) all += pow[i];
    // Полуширина главного лепестка Ханна (2/T) с запасом на плавание ритма.
    var hw = Math.max(0.08, 1.5 / (n / fps));
    var snr = all > 0 ? (bandPow(pow, f0, hw) + bandPow(pow, 2 * f0, hw)) / all : 0;
    var bpm = f0 * 60;

    // Качество 0..1 — для полоски на экране: ниже 0.5 страница предупреждает,
    // что число может быть неточным. 0.5 здесь — SNR 0.6: на окнах 30 с
    // точность между 0.55 и 0.6 — 90 %, выше 0.6 — 96 %.
    var quality = Math.max(0, Math.min(1, (snr - 0.5) / 0.2));
    var res = { bpm: bpm, quality: quality, snr: snr, rms: rms };
    var need = opts && opts.prelim ? PRELIM_SNR : FINAL_SNR;
    if (snr < need) { res.ok = false; res.reason = 'noisy'; return res; }
    if (bpm < MIN_BPM || bpm > MAX_BPM) { res.ok = false; res.reason = 'out_of_range'; return res; }
    res.ok = true;
    return res;
  }

  /**
   * Приведение ряда к РАВНОМЕРНОЙ сетке времени.
   *
   * ⚠️ Зачем: `estimate()` считает, что между соседними отсчётами одинаковый
   * интервал. Кадры с камеры так не приходят: страница подтормаживает,
   * телефон греется, вспышка съедает такты. Замерено 14.08 на ролике с
   * ИЗВЕСТНЫМИ 72 уд/мин: на лёгком вьюпорте тул давал ровно 72, а на
   * тяжёлом — 78 и 80. Ошибка рождалась не в математике периода, а в
   * допущении о равномерности.
   *
   * Линейная интерполяция по фактическим меткам времени. Наибольший разрыв
   * между кадрами сообщаем наружу.
   *
   * @param {Array<number>} values  отсчёты
   * @param {Array<number>} timesMs метки времени тех же отсчётов, мс
   * @param {number} fps            частота целевой сетки
   * @returns {{values:Float64Array, fps:number, gapMs:number}|null}
   */
  function resample(values, timesMs, fps) {
    var n = values.length;
    if (!timesMs || timesMs.length !== n || n < 4) return null;
    var span = timesMs[n - 1] - timesMs[0];
    if (!(span > 0)) return null;

    var step = 1000 / fps;
    var out = new Float64Array(Math.floor(span / step) + 1);
    var gap = 0, j = 0;
    for (var i = 1; i < n; i++) {
      var d = timesMs[i] - timesMs[i - 1];
      if (d > gap) gap = d;
    }
    for (var k = 0; k < out.length; k++) {
      var t = timesMs[0] + k * step;
      while (j < n - 2 && timesMs[j + 1] < t) j++;
      var t0 = timesMs[j], t1 = timesMs[j + 1], dt = t1 - t0;
      var w = dt > 0 ? (t - t0) / dt : 0;
      if (w < 0) w = 0; else if (w > 1) w = 1;
      out[k] = values[j] + (values[j + 1] - values[j]) * w;
    }
    return { values: out, fps: fps, gapMs: gap };
  }

  /**
   * Оценка по ряду С МЕТКАМИ ВРЕМЕНИ — то, чем пользуется страница.
   * Сначала выравнивает сетку (см. resample), потом считает как обычно.
   * Сетка — 30 Гц: полоса кончается на 3.5 Гц, гуще считать незачем, а
   * спектр на 60 Гц вдвое дороже на слабом телефоне.
   */
  function estimateTimed(values, timesMs, opts) {
    var n = values.length;
    if (!timesMs || timesMs.length !== n || n < 4) return { ok: false, reason: 'too_short' };
    var span = (timesMs[n - 1] - timesMs[0]) / 1000;
    if (span <= 0) return { ok: false, reason: 'too_short' };
    var meanFps = (n - 1) / span;
    var grid = Math.max(10, Math.min(30, Math.round(meanFps * 1.2)));
    var rs = resample(values, timesMs, grid);
    if (!rs) return { ok: false, reason: 'too_short' };
    var r = estimate(rs.values, rs.fps, opts);
    r.gapMs = rs.gapMs;
    r.fps = meanFps;
    return r;
  }

  /**
   * Пульс из ручных постукиваний — запасной путь для десктопа без камеры и
   * способ, которым врачи и считают: по интервалам между ударами.
   * Возвращает также разброс интервалов: большой разброс = сбились, а не аритмия.
   */
  function fromTaps(timestampsMs) {
    if (!timestampsMs || timestampsMs.length < 4) return { ok: false, reason: 'too_few_taps' };
    var iv = [];
    for (var i = 1; i < timestampsMs.length; i++) iv.push(timestampsMs[i] - timestampsMs[i - 1]);
    // Отбрасываем явные промахи (двойной тап, пауза) — медиана ±60 %.
    var sorted = iv.slice().sort(function (a, b) { return a - b; });
    var med = sorted[sorted.length >> 1];
    var kept = iv.filter(function (x) { return x > med * 0.4 && x < med * 1.6; });
    if (kept.length < 3) return { ok: false, reason: 'irregular_taps' };
    var mean = kept.reduce(function (a, b) { return a + b; }, 0) / kept.length;
    if (mean <= 0) return { ok: false, reason: 'irregular_taps' };
    var sd = Math.sqrt(kept.reduce(function (a, b) { return a + (b - mean) * (b - mean); }, 0) / kept.length);
    var bpm = 60000 / mean;
    if (bpm < MIN_BPM || bpm > MAX_BPM) return { ok: false, reason: 'out_of_range' };
    return { ok: true, bpm: bpm, spreadMs: sd, taps: timestampsMs.length };
  }

  /**
   * Классификация ОДНОГО кадра: похож ли он на палец, прижатый к объективу.
   * Раньше эти пороги жили внутри UI-цикла и проверялись только ПОСЛЕ 25 секунд
   * записи — человек узнавал «пальца не было» в самом конце. Теперь функция
   * чистая: UI зовёт её на каждом кадре и ведёт человека ДО старта отсчёта.
   *
   * Физика порогов (замерено на двух контролях, см. историю тула):
   *                       R/G     простр. разброс
   *   узор Chromium       0.18        1.341
   *   палец (синтет.)     5.10        0.007
   * Гемоглобин гасит зелёный — палец всегда красный; и он закрывает объектив
   * целиком — кадр однороден. Пороги стоят с большим запасом от обеих сторон.
   *
   * @param {number} red    средний красный канал кадра (0..255)
   * @param {number} green  средний зелёный канал кадра (0..255)
   * @param {number} spread нормированный простр. разброс красного (sd/mean)
   * @returns {{finger:boolean, dark:boolean}} dark — кадр слишком тёмный,
   *          чтобы судить (ноутбук без вспышки в тёмной комнате): это ДРУГАЯ
   *          подсказка человеку («добавьте света»), а не «уберите палец».
   */
  function fingerFrame(red, green, spread) {
    var dark = red < 26;
    var finger = !dark && red >= green * 1.3 && spread <= 0.6;
    return { finger: finger, dark: dark };
  }

  return {
    MIN_BPM: MIN_BPM, MAX_BPM: MAX_BPM, FINAL_SNR: FINAL_SNR, PRELIM_SNR: PRELIM_SNR,
    estimate: estimate, estimateTimed: estimateTimed, resample: resample,
    fromTaps: fromTaps, fingerFrame: fingerFrame,
    _bandpass: bandpass
  };
}));
